import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert,
} from '@mui/material';
import { FileUpload } from '@mui/icons-material';
import type { CreateTestCatalogItemRequest } from '../../../types/testCatalogItem';

interface TestCatalogImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImport: (items: CreateTestCatalogItemRequest[]) => void;
}

const EXPECTED_HEADERS = [
  'TestName',
  'AstmCode',
  'Description',
  'Category',
  'BstCode',
  'UnitPriceCad',
  'Unit',
];

const PREVIEW_ROWS = 10;

const parseCsvLine = (line: string): string[] => {
  const separator = line.includes('\t') ? '\t' : ',';
  return line.split(separator).map((v) => v.trim().replace(/^["']|["']$/g, ''));
};

export const TestCatalogImportDialog = ({
  open,
  onClose,
  onImport,
}: TestCatalogImportDialogProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [parsedItems, setParsedItems] = useState<CreateTestCatalogItemRequest[]>([]);
  const [errors, setErrors] = useState<string[]>([]);

  const resetState = useCallback(() => {
    setFile(null);
    setParsedItems([]);
    setErrors([]);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (!selected) return;

      setFile(selected);
      setErrors([]);

      const reader = new FileReader();
      reader.onload = (evt) => {
        const text = evt.target?.result as string;
        if (!text) return;

        const lines = text
          .split(/\r?\n/)
          .map((l) => l.trim())
          .filter(Boolean);

        if (lines.length < 2) {
          setErrors(['File must have a header row and at least one data row.']);
          return;
        }

        const headers = parseCsvLine(lines[0]);
        const missing = EXPECTED_HEADERS.filter(
          (h) => !headers.some((hdr) => hdr.toLowerCase() === h.toLowerCase()),
        );

        if (missing.length > 0) {
          setErrors([`Missing columns: ${missing.join(', ')}`]);
          return;
        }

        const headerMap = Object.fromEntries(
          headers.map((h, i) => [h.toLowerCase(), i]),
        );

        const items: CreateTestCatalogItemRequest[] = [];
        const parseErrors: string[] = [];

        for (let i = 1; i < lines.length; i++) {
          const values = parseCsvLine(lines[i]);
          const testName = values[headerMap['testname']] || '';

          if (!testName) {
            parseErrors.push(`Row ${i + 1}: missing TestName`);
            continue;
          }

          const price = parseFloat(values[headerMap['unitpricecad']] || '0');
          if (isNaN(price)) {
            parseErrors.push(`Row ${i + 1}: invalid UnitPriceCad`);
            continue;
          }

          items.push({
            TestName: testName,
            AstmCode: values[headerMap['astmcode']] || undefined,
            Description: values[headerMap['description']] || undefined,
            Category: values[headerMap['category']] || undefined,
            BstCode: values[headerMap['bstcode']] || undefined,
            UnitPriceCad: price,
            Unit: values[headerMap['unit']] || 'Each',
          });
        }

        setParsedItems(items);
        setErrors(parseErrors);
      };

      reader.readAsText(selected);
    },
    [],
  );

  const previewItems = parsedItems.slice(0, PREVIEW_ROWS);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Test Catalogue from CSV</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Button variant="outlined" component="label" startIcon={<FileUpload />}>
            {file ? file.name : 'Select CSV File'}
            <input type="file" accept=".csv,.tsv,.txt" hidden onChange={handleFileChange} />
          </Button>

          <Typography variant="body2" color="text.secondary">
            Expected columns: {EXPECTED_HEADERS.join(', ')}
          </Typography>

          {errors.length > 0 && (
            <Alert severity="warning">
              {errors.length} error(s) found.{' '}
              {errors.slice(0, 5).map((err, i) => (
                <Box key={i} component="span" sx={{ display: 'block' }}>
                  {err}
                </Box>
              ))}
              {errors.length > 5 && `...and ${errors.length - 5} more`}
            </Alert>
          )}

          {previewItems.length > 0 && (
            <>
              <Typography variant="subtitle2">
                Preview ({parsedItems.length} rows total, showing first {previewItems.length})
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Test Name</TableCell>
                      <TableCell>ASTM</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>BST Code</TableCell>
                      <TableCell align="right">Price (CAD)</TableCell>
                      <TableCell>Unit</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewItems.map((item, i) => (
                      <TableRow key={i}>
                        <TableCell>{item.TestName}</TableCell>
                        <TableCell>{item.AstmCode || '-'}</TableCell>
                        <TableCell>{item.Category || '-'}</TableCell>
                        <TableCell>{item.BstCode || '-'}</TableCell>
                        <TableCell align="right">{item.UnitPriceCad.toFixed(2)}</TableCell>
                        <TableCell>{item.Unit}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={parsedItems.length === 0}
          onClick={() => onImport(parsedItems)}
        >
          Import {parsedItems.length} Item(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
};
